import * as _ from 'lodash'
import { IBusinessObject } from '../PA.Core/PA.Core.IF.BO'
import { IMessageProvider, IProgressProvider, MessageType } from '../PA.Core/PA.Core.IF.Basic'
import { PAdbContext } from '../PA.Core/PAdbContext'
import { IdentityProvider } from '../PA.Core/Basic/Identity'
import { MessageProvider } from '../PA.Core/Basic/Messages'
import { ProgressProvider } from '../PA.Core/Basic/Progress'
import {
    WB_WorkflowRow, WB_WorkflowStatusRelationshipRow, WB_WorkflowStatusRow
} from '../PA.Core/PAdbSchemaRows'
import { IwbWorkflow } from '../PA.Core/PAdbSchemaRowsIF'
import {
    esWB_Status_Relationship,
    esWB_Workflow, esWB_Workflow_Status
} from '../PA.Core/PAdbSchema'
import { ESDataStore } from '../PA.Core/lib/ESDataStore'
import { ESRecord } from '../PA.Core/lib/ESRecord'
import { CreateOptions, ESRecordResolver } from '../PA.Core/lib/ESRecordResolver'
import { ASync } from '../PA.Core/Basic/ASync'
import { WorkflowBOValidations } from './WorkflowBO.Validations'
import { PAEnvironment } from '../PA.Core/PAEnvironment'


export interface IWorkflowLoadOptions {
    dbContext?: PAdbContext
}

const WorkflowLoadOptions_default: IWorkflowLoadOptions = {}

export class WorkflowBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider

    CreateOptions: any

    LoadOptions: any
    _bIsLoaded: boolean
    _OnLoadComplete: Promise<IBusinessObject>
    LoadProgressProvider: IProgressProvider

    ValidateOptions: any
    _bIsValidated: boolean
    ValidateMessageProvider: IMessageProvider
    ValidateProgressProvider: IProgressProvider

    SaveOptions: any
    _bIsSaved: boolean
    SaveProgressProvider: IProgressProvider

    DeleteOptions: any
    _bIsDeleted: boolean
    DeleteMessageProvider: IMessageProvider
    DeleteProgressProvider: IProgressProvider

    dbContext: PAdbContext
    drWB_Workflow: WB_WorkflowRow

    constructor(options?: any) {

        this.CreateOptions = options || {}

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext

        this.Identity = new IdentityProvider({ id: 0, name: 'New Workflow' })

        this.LoadOptions = {}
        this._bIsLoaded = false
        this._OnLoadComplete = undefined
        this.LoadProgressProvider = new ProgressProvider('Loading', this)

        this.ValidateOptions = {}
        this._bIsValidated = false
        this.ValidateMessageProvider = new MessageProvider()
        this.ValidateProgressProvider = new ProgressProvider('Validating', this)

        this.SaveOptions = {}
        this._bIsSaved = false
        this.SaveProgressProvider = new ProgressProvider('Saving', this)

        this.DeleteOptions = {}
        this._bIsDeleted = false
        this.DeleteMessageProvider = new MessageProvider()
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this)

        this.drWB_Workflow = undefined
    }

    //</editor-fold>

    get WB_Workflow(): WB_WorkflowRow {
        return this.drWB_Workflow
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this
        this.LoadProgressProvider.start('Creating Workflow')

        let createOpts = _.defaultsDeep({}, self.CreateOptions, options)

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {}
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else
                self.dbContext = new PAdbContext()

            if (this.dbContext == undefined) {
                var sError = 'Unable to determine dbContext for new Workflow.'
                self.LoadProgressProvider.exception(sError)
                error(sError)
                return
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_Workflow)
                .then((newWorkflowRow: WB_WorkflowRow) => {
                    newWorkflowRow.setDefaults()
                    self.drWB_Workflow = newWorkflowRow
                    self.dbContext.add(esWB_Workflow, newWorkflowRow)
                    self.Identity.setAll(self.WB_Workflow.cif.WB_WORKFLOW_ID, self.WB_Workflow.cif.WORKFLOW_ID, self.WB_Workflow.cif.WORKFLOW_NAME)
                    self.LoadProgressProvider.setName('Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')')
                    self.Identity.listenToRecordUpdates(self.WB_Workflow, 'WB_WORKFLOW_ID', 'WORKFLOW_ID', 'WORKFLOW_NAME')

                    self.LoadProgressProvider.finish()
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    resolve(self)

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        })

        self._OnLoadComplete = internalOnComplete

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start('Loading (' + anId + ')')

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception('Workflow already loaded.')
                resolve(self) // "Workflow already loaded.";
                return
            }

            self.LoadOptions = options || {}
            _.defaults(self.LoadOptions, WorkflowLoadOptions_default)


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({})
            }

            if (_.isEmpty(self.dbContext)) {
                error('Unable to determine dbContext for Workflow.')
                return
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = WorkflowBO.createRecordResolver({ dbContext: self.dbContext })
                recordResolverLoadPromise = WorkflowBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function() {
                    let aWorkflowRow: any = recordResolver.get(esWB_Workflow, anId)

                    if (_.isEmpty(aWorkflowRow)) {
                        error('Workflow not Found')
                        return
                    }
                    self.drWB_Workflow = aWorkflowRow
                    self.Identity.setAll(self.WB_Workflow.cif.WB_WORKFLOW_ID, self.WB_Workflow.cif.WORKFLOW_ID, self.WB_Workflow.cif.WORKFLOW_NAME)
                    self.LoadProgressProvider.setName('Loading ' + self.Identity.name + '(' + self.Identity.id_ui + ')')
                    self.Identity.listenToRecordUpdates(self.WB_Workflow, 'WB_WORKFLOW_ID', 'WORKFLOW_ID', 'WORKFLOW_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function(err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return
            })

        })

        return this._OnLoadComplete
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded
    }


    Validate(options?: any): Promise<IBusinessObject> {

        var self = this
        this.ValidateMessageProvider.clearMessages()
        this.ValidateProgressProvider.start('Validating ' + self.WB_Workflow.cif.WORKFLOW_NAME + '(' + self.WB_Workflow.cif.WORKFLOW_ID + ')')
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {}

            if (self.dbContext == undefined) {
                var sError = 'Unable to determine dbContext for to Validate Workflow.'
                self.ValidateProgressProvider.exception(sError)
                reject(sError)
            }

            WorkflowBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false
                    } else {
                        self._bIsValidated = true
                    }
                    self.ValidateProgressProvider.finish()
                    resolve(self)

                })
                .catch((err) => {
                    self._bIsValidated = false
                    self.ValidateProgressProvider.exception(err)
                    resolve(self)

                })
        })
    }

    get bIsValidated(): boolean {
        return this._bIsValidated
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this
        var wbTxn = <IwbWorkflow>self.drWB_Workflow
        this.SaveProgressProvider.start('Saving ' + wbTxn.WORKFLOW_NAME + '(' + wbTxn.WORKFLOW_ID + ')')

        return new Promise<IBusinessObject>((resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false
                var sError = 'Unable to determine dbContext to Save Workflow.'
                self.SaveProgressProvider.exception(sError)
                reject(sError)
                return
            }

            self.SaveOptions = options || {}

            var saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            // Add code to do individual BO save for dbContextRows....
            self.Validate({})
                .then((ignore) => {
                    if (!self.bIsValidated) {
                        self._bIsSaved = false
                        self.SaveProgressProvider.finish()
                        resolve(self)
                        return
                    }
                    return saveFunction(esWB_Workflow, self.drWB_Workflow, {})
                })
                .then((ignore) => {
                    var tasks: Promise<any>[] = []
                    tasks.push(saveFunction(esWB_Workflow_Status, self.drWB_Workflow.WB_WorkflowStatusRows))

                    tasks.push(saveFunction(esWB_Status_Relationship, _.flatMap(self.drWB_Workflow.WB_WorkflowStatusRows,
                        "WB_WorkflowStatusRelationshipRows")))

                    return Promise.all(tasks)
                })
                .then((ignored) => {
                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true
                        })
                    } else {
                        self._bIsSaved = true
                    }
                    self.SaveProgressProvider.finish()
                    resolve(self)
                })
                .catch((err) => {
                    self.SaveProgressProvider.exception(err)
                    resolve(self)
                })


        })
    }


    get bIsSaved(): boolean {
        return this._bIsSaved
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this
        var wbWorkflow = <IwbWorkflow>self.drWB_Workflow
        this.DeleteMessageProvider.clearMessages()
        this.DeleteProgressProvider.start('Deleting ' + wbWorkflow.WORKFLOW_NAME + '(' + wbWorkflow.WB_WORKFLOW_ID + ')')
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Workflow

            // 2. If Valid, mark all the records for deletion.
            self.drWB_Workflow.markDeleted()

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true
                        self.DeleteProgressProvider.finish()
                        resolve(self)

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true
                self.DeleteProgressProvider.finish()
                resolve(self)
            }
        })
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted
    }

    //------------Workflow ITEMS---------------------------------------------------
//<editor-fold desc = "Workflow Items">
    addStatus(aStatusId: number): Promise<IBusinessObject> {

        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            var aStatusRow: WB_WorkflowStatusRow = undefined

            self.dbContext.createRecordWithKey(esWB_Workflow_Status)
                .then((aNewRow: WB_WorkflowStatusRow) => {
                    aNewRow.setDefaults()
                    aStatusRow = aNewRow
                    aStatusRow.cif.WB_WORKFLOW_ID = self.drWB_Workflow.cif.WB_WORKFLOW_ID
                    self.dbContext.add(esWB_Workflow_Status, aStatusRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeStatus(aStatusId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_Workflow.WB_WorkflowStatusRows, (aStatusRow: WB_WorkflowStatusRow) => {
                if (aStatusRow.isActive && aStatusRow.cif.WB_STATUS_ID == aStatusId) {
                    _.forEach(aStatusRow.WB_WorkflowStatusRelationshipRows, (aStatusRelRow: WB_WorkflowStatusRelationshipRow) => {
                        aStatusRelRow.markDeleted()
                    })
                    aStatusRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

//</editor-fold>

    //<editor-fold desc = "Workflow Status Operations">

    addTransition(aFromStatusId: number, aToStatusId: number): Promise<IBusinessObject> {
        const self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            // first find out if record exists.
            var found = false

            var aStatusRelRow: WB_WorkflowStatusRelationshipRow = undefined
            var aFromStatusRow: WB_WorkflowStatusRow = undefined
            var aNextStatusRow: WB_WorkflowStatusRow = undefined

            self.dbContext.find(esWB_Workflow_Status, aFromStatusId)
                .then((aFromRecord: WB_WorkflowStatusRow) => {
                    if (_.isEmpty(aFromRecord)) {
                        // Bad Status Id...
                        resolve(self)
                        return
                    }
                    aFromStatusRow = aFromRecord

                    self.dbContext.find(esWB_Workflow_Status, aToStatusId)
                        .then((aToRecord: WB_WorkflowStatusRow) => {
                            if (_.isEmpty(aToRecord)) {
                                // Bad Status Id...
                                resolve(self)
                                return
                            }
                            aNextStatusRow = aToRecord
                            // create new records
                            return self.dbContext.createRecordWithKey(esWB_Status_Relationship)
                        })
                        .then((aNewStatusRelationship: WB_WorkflowStatusRelationshipRow) => {
                            aStatusRelRow = aNewStatusRelationship
                            aStatusRelRow.cif.WB_STATUS_ID = aFromStatusId
                            aStatusRelRow.cif.WB_STATUS_ID_NEXT = aToStatusId
                            aStatusRelRow.WB_WorkflowStatusRow = aFromStatusRow
                            aStatusRelRow.WB_WorkflowStatusRow_Next = aNextStatusRow
                            self.dbContext.add(esWB_Status_Relationship, aStatusRelRow)
                            resolve(self)
                        })
                        .catch((err) => {
                            error(err)
                        })

                })
                .catch((err) => {
                    error(err)
                })
        })
    }


    removeTransition(aStatusRelId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_Workflow.WB_WorkflowStatusRows, (aStatusRow: WB_WorkflowStatusRow) => {
                _.forEach(aStatusRow.WB_WorkflowStatusRelationshipRows, (aStatusRelRow: WB_WorkflowStatusRelationshipRow) => {
                    if (aStatusRelRow.isActive && aStatusRelRow.cif.WB_STATUSR_ID == aStatusRelId) {
                        aStatusRelRow.markDeleted()
                    }
                })
            })

            resolve(self)
        })
    }

    //</editor-fold>

//</editor-fold>


    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext
        } else {
            dbContext = new PAdbContext({})
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_Workflow, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10
    }


}
PAEnvironment.registerBusinessObjectClass(WorkflowBO, 'WorkflowBO', 'Workflow')